import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FaChartBar } from 'react-icons/fa';
import { DataTable, EmptyState, Pill, ProgressBar, StatStrip } from '../../components/EntityList';
import { showCurrency } from '../../utils';
import { summarize } from './predictionProjection';
import MonthlyBars from './MonthlyBars';
import { MODULE_CLASSES } from './predictionTypes';

const ACCENT_VAR = {
	'cat-1': '--color-cat-1',
	'cat-5': '--color-cat-5',
};

function formatMoney(monto, currency) {
	return `${showCurrency(currency) || ''} ${Math.round(monto).toLocaleString('es-UY')}`.trim();
}

/**
 * Left-column projection preview: dashed empty state when no type is
 * selected, otherwise a panel with header pill, KPI strip, monthly bars,
 * and a breakdown table — plus a collapsible row-level detail section.
 */
function ProjectionPanel({ type, months, moneda, horizonteLabel, detailRows, showDetail, onToggleDetail }) {
	const [barCurrency, setBarCurrency] = useState('uyu');

	const currencies = useMemo(() => {
		if (moneda === 'UYU') return ['uyu'];
		if (moneda === 'USD') return ['usd'];
		return ['uyu', 'usd'];
	}, [moneda]);

	if (!type) {
		return (
			<div className='tw:rounded-panel tw:border tw:border-dashed tw:border-border-strong tw:bg-surface'>
				<EmptyState
					icon={<FaChartBar />}
					title='Sin proyección todavía'
					description='Elegí un tipo de previsión arriba para ver el flujo proyectado por mes.'
				/>
			</div>
		);
	}

	if (!months || months.length === 0) {
		return (
			<div className='tw:rounded-panel tw:border tw:border-border tw:bg-surface'>
				<EmptyState title='Cargando proyección…' />
			</div>
		);
	}

	const { Icon, labels, module } = type;
	const moduleClasses = MODULE_CLASSES[module];
	const accentVar = ACCENT_VAR[module];
	const activeCurrency = currencies.includes(barCurrency) ? barCurrency : currencies[0];

	const summaries = currencies.reduce((acc, c) => {
		acc[c] = summarize(months, c);
		return acc;
	}, {});

	const stats = [];
	if (currencies.length === 1) {
		const s = summaries[activeCurrency];
		stats.push({
			label: labels.totalLabel,
			value: formatMoney(s.total, activeCurrency === 'usd' ? 'USD' : 'UYU'),
			mono: true,
			sub: `${s.n} ${labels.countUnit}`,
			accent: accentVar,
		});
	} else {
		stats.push({
			label: `${labels.totalLabel} · $`,
			value: formatMoney(summaries.uyu.total, 'UYU'),
			mono: true,
			sub: `${summaries.uyu.n} ${labels.countUnit}`,
			accent: accentVar,
		});
		stats.push({
			label: `${labels.totalLabel} · US$`,
			value: formatMoney(summaries.usd.total, 'USD'),
			mono: true,
		});
	}
	const activeSummary = summaries[activeCurrency];
	stats.push({ label: 'Promedio mensual', value: formatMoney(activeSummary.prom, activeCurrency === 'usd' ? 'USD' : 'UYU'), mono: true, sub: `sobre ${months.length} meses` });
	stats.push({ label: 'Mes de mayor monto', value: activeSummary.pico?.label ?? '—', sub: formatMoney(activeSummary.pico?.[activeCurrency].monto ?? 0, activeCurrency === 'usd' ? 'USD' : 'UYU'), mono: false });

	const columns = [
		{ key: 'mes', header: 'Mes', render: (m) => m.label },
		{ key: 'n', header: labels.countUnit === 'cuotas' ? 'Cuotas' : 'Operaciones', className: 'tw:text-right tw:font-mono', render: (m) => currencies.reduce((s, c) => s + m[c].n, 0) },
	];
	currencies.forEach((c) => {
		columns.push({
			key: `monto-${c}`,
			header: currencies.length > 1 ? `${labels.amountHeader} ${c === 'usd' ? 'US$' : '$'}` : labels.amountHeader,
			className: 'tw:text-right tw:font-mono tw:font-medium',
			render: (m) => (
				<span style={m.mes === summaries[c].pico?.mes ? { color: `var(${accentVar})` } : undefined}>
					{formatMoney(m[c].monto, c === 'usd' ? 'USD' : 'UYU')}
				</span>
			),
		});
	});
	columns.push({
		key: 'peso',
		header: 'Peso relativo',
		className: 'tw:w-[32%]',
		render: (m) => <ProgressBar value={m[activeCurrency].monto} max={summaries[activeCurrency].maxMonto} accent={accentVar} />,
	});

	const footerRow = { mes: 'Total', __footer: true };
	currencies.forEach((c) => {
		footerRow[c] = { monto: summaries[c].total, n: summaries[c].n };
	});
	const footerColumns = columns.map((col) => ({
		...col,
		footerRender: () => {
			if (col.key === 'mes') return 'Total';
			if (col.key === 'n') return currencies.reduce((s, c) => s + summaries[c].n, 0);
			if (col.key === 'peso') return null;
			const c = col.key.replace('monto-', '');
			return (
				<span style={{ color: `var(${accentVar})` }}>{formatMoney(summaries[c].total, c === 'usd' ? 'USD' : 'UYU')}</span>
			);
		},
	}));

	return (
		<div className='tw:rounded-panel tw:border tw:border-border tw:bg-surface'>
			<div className='tw:flex tw:items-center tw:gap-3 tw:border-b tw:border-border tw:px-5 tw:py-4'>
				<span className={classnames('tw:flex tw:h-7 tw:w-7 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:text-white', moduleClasses.tile)}>
					<Icon size={15} />
				</span>
				<h3 className='tw:m-0 tw:text-[14px] tw:font-semibold tw:text-ink-0'>Proyección · {labels.panelTitle}</h3>
				<span className='tw:flex-1' />
				<Pill tone='neutral'>{horizonteLabel}</Pill>
			</div>

			<StatStrip stats={stats} bare />

			{currencies.length > 1 && (
				<div className='tw:flex tw:justify-end tw:gap-1 tw:px-6 tw:pt-4'>
					{['uyu', 'usd'].map((c) => (
						<button
							key={c}
							type='button'
							onClick={() => setBarCurrency(c)}
							className={classnames(
								'tw:rounded-[6px] tw:border tw:px-2.5 tw:py-1 tw:text-[11px] tw:font-semibold tw:transition-colors tw:duration-100',
								activeCurrency === c
									? 'tw:border-accent tw:bg-accent-soft tw:text-accent-ink'
									: 'tw:border-border tw:bg-surface tw:text-ink-2 tw:hover:bg-surface-2',
							)}
						>
							{c === 'usd' ? 'US$' : '$'}
						</button>
					))}
				</div>
			)}

			<MonthlyBars months={months} currency={activeCurrency} maxMonto={summaries[activeCurrency].maxMonto} peakMes={summaries[activeCurrency].pico?.mes} moduleClasses={moduleClasses} />

			<div className='tw:border-t tw:border-border tw:pt-2'>
				<DataTable columns={footerColumns} rows={months} rowKey={(m) => m.mes} footerRow={footerRow} bare />
			</div>

			<div className='tw:border-t tw:border-border tw:px-5 tw:py-3'>
				<button
					type='button'
					onClick={onToggleDetail}
					className='tw:border-0 tw:bg-transparent tw:p-0 tw:text-[12px] tw:font-medium tw:text-accent tw:hover:underline'
				>
					{showDetail ? 'Ocultar detalle de cuotas' : 'Ver detalle de cuotas'}
				</button>
				{showDetail && (
					<div className='tw:mt-3'>{detailRows}</div>
				)}
			</div>
		</div>
	);
}

ProjectionPanel.propTypes = {
	type: PropTypes.object,
	months: PropTypes.array,
	moneda: PropTypes.oneOf(['all', 'UYU', 'USD']),
	horizonteLabel: PropTypes.string,
	detailRows: PropTypes.node,
	showDetail: PropTypes.bool,
	onToggleDetail: PropTypes.func,
};

export default ProjectionPanel;
